/**
 * Application identity and navigation bar. Reads date/view and user preferences from stores,
 * exposes previous/today/next actions, activity creation, command palette, theme toggling, and
 * authenticated logout.
 *
 * Opaque navigation surfaces avoid backdrop sampling during every scroll and interaction.
 * See docs/developer/ui-latency.md for the measured rendering tradeoff.
 *
 * @module apps/web/src/components/layout/Header
 */

import { useScheduleStore } from '@/stores/scheduleStore';
import { useUserStore } from '@/stores/userStore';
import { Button } from '@/components/ui/button';
import { openNewActivityFlow } from '@/components/activities/NewActivityFlow';
import { ChevronLeft, ChevronRight, Command, LogOut, Moon, Orbit, Plus, Sun } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { UserAvatar } from '@/components/ui/user-avatar';

/**
 * Application identity and navigation bar. Reads date/view and user preferences from stores,
 * exposes previous/today/next actions, activity creation, command palette, theme toggling, and
 * authenticated logout.
 */
export function Header() {
  const currentDate = useScheduleStore((s) => s.currentDate);
  const view = useScheduleStore((s) => s.view);
  const goToToday = useScheduleStore((s) => s.goToToday);
  const goToPrevious = useScheduleStore((s) => s.goToPrevious);
  const goToNext = useScheduleStore((s) => s.goToNext);
  const toggleCommandPalette = useScheduleStore((s) => s.toggleCommandPalette);
  
  const settings = useUserStore((s) => s.settings);
  const updateSettings = useUserStore((s) => s.updateSettings);
  const displayName = useUserStore((s) => s.displayName);
  const email = useUserStore((s) => s.email);
  const avatarUrl = useUserStore((s) => s.avatarUrl);
  const { logout } = useAuth();

  const toggleTheme = () => {
    const newTheme = settings.theme === 'dark' ? 'light' : 'dark';
    updateSettings({ theme: newTheme });
  };

  const getDateTitle = () => {
    switch (view) {
      case 'day':
        return format(currentDate, 'EEEE, MMMM d, yyyy');
      case 'week':
        return format(currentDate, 'MMMM yyyy');
      case 'month':
        return format(currentDate, 'MMMM yyyy');
      default:
        return format(currentDate, 'MMMM yyyy');
    }
  };

  return (
    <header className="flex min-h-[calc(3.5rem+env(safe-area-inset-top))] items-center justify-between gap-2 border-b bg-background px-3 pb-2 pt-[calc(0.5rem+env(safe-area-inset-top))] shadow-sm sm:min-h-[calc(4rem+env(safe-area-inset-top))] sm:px-4 md:px-6">
      <div className="min-w-0 flex-1 md:flex-none">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-[0_0_28px_hsl(var(--primary)/0.32)]">
            <Orbit className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold sm:text-xl">Fluxability</h1>
            <p className="hidden text-[11px] text-muted-foreground sm:block">Energy in motion</p>
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2 md:gap-4">
        <Button variant="outline" size="sm" onClick={goToPrevious}>
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <Button variant="outline" size="sm" onClick={goToToday} className="hidden sm:inline-flex">
          Today
        </Button>

        <Button variant="outline" size="sm" onClick={goToNext}>
          <ChevronRight className="h-4 w-4" />
        </Button>

        <div className="hidden min-w-[120px] text-center sm:block md:min-w-[200px]">
          <h2 className="text-sm font-semibold md:text-lg">{getDateTitle()}</h2>
        </div>
      </div>

      <div className="flex min-w-0 flex-1 items-center justify-end gap-1 sm:gap-2 md:flex-none">
        <Button
          size="sm"
          onClick={() => openNewActivityFlow('choose')}
          className="hidden h-9 gap-1.5 px-2 sm:inline-flex sm:h-10 sm:px-3"
        >
          <Plus className="h-4 w-4" />
          <span>New</span>
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={toggleCommandPalette}
          title="Command Palette (Cmd+K)"
          className="h-9 w-9 sm:h-10 sm:w-10"
        >
          <Command className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={toggleTheme}
          title="Toggle theme"
          className="hidden h-9 w-9 sm:inline-flex sm:h-10 sm:w-10"
        >
          {settings.theme === 'dark' ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </Button>

        <UserAvatar name={displayName} email={email} avatarUrl={avatarUrl} className="h-9 w-9 sm:h-10 sm:w-10 lg:hidden" />
        <div className="hidden max-w-[220px] items-center gap-2 rounded-md border bg-background/70 py-1 pl-1 pr-3 shadow-sm lg:flex">
          <UserAvatar name={displayName} email={email} avatarUrl={avatarUrl} className="h-7 w-7" />
          <span className="truncate text-sm">{displayName || email || 'User'}</span>
        </div>
        <Button variant="outline" size="icon" onClick={logout} title="Logout" className="h-9 w-9 sm:h-10 sm:w-10">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}
